import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from '@mui/joy';
import { CheckCircle, XCircle as ErrorIcon } from '@phosphor-icons/react';
import { authService } from '../../lib/auth';

type VerificationStatus = 'loading' | 'success' | 'error';

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      const response = await fetch(`/api/signup/verify/?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Store the JWT access token (refresh token is stored in httpOnly cookie)
        if (data.access) {
          authService.setAccessToken(data.access);
        }
        setStatus('success');

        // Redirect to setup wizard after a short delay (new users)
        setTimeout(() => {
          navigate('/setup');
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'Verification failed. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('An error occurred during verification. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.level1',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent>
          <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
            {status === 'loading' && (
              <>
                <CircularProgress size="lg" />
                <Typography level="h4" textAlign="center">
                  Verifying your email...
                </Typography>
                <Typography level="body-md" textAlign="center" color="neutral">
                  Please wait while we confirm your email address.
                </Typography>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle size={64} color="var(--joy-palette-success-500)" />
                <Typography level="h4" textAlign="center">
                  Email Verified!
                </Typography>
                <Alert color="success" sx={{ width: '100%' }}>
                  Your email has been verified successfully. Starting Quick Setup...
                </Alert>
                <CircularProgress size="sm" />
              </>
            )}

            {status === 'error' && (
              <>
                <ErrorIcon size={64} color="var(--joy-palette-danger-500)" />
                <Typography level="h4" textAlign="center">
                  Verification Failed
                </Typography>
                <Alert color="danger" sx={{ width: '100%' }}>
                  {errorMessage}
                </Alert>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="outlined"
                    color="primary"
                  >
                    Go to Login
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/signup"
                    variant="solid"
                    color="primary"
                  >
                    Sign Up Again
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
